import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwDialogModule } from '@cdw-ae/common-ui-components-dialog';
import { CdwFileUploadModule } from '@cdw-ae/common-ui-components-file-upload';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CommonUiComponentsSpinnerComponent } from '@cdw-ae/common-ui-components-spinner';
import { CdwStepIndicatorComponent } from '@cdw-ae/common-ui-components-step-indicator';
import { ApiUrlBuilderService } from '../../../../../../../common/services/api-url-builder.service';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '@cdw-ae/snackbar';
import type { CsvValidationResult, CsvValidationWarning } from '@cdw-ae/common-data';
import { CsvValidationService } from '../../../../../../../common/services/csv-validation.service';
import { TailwindColors } from '../../../../../../../common/constants/tailwind-colors';

@Component({
  selector: 'user-collection-csv-upload-dialog',
  templateUrl: './user-collection-csv-upload-dialog.component.html',
  styleUrls: ['./user-collection-csv-upload-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatRadioModule,
    CdwButtonModule,
    CdwStepIndicatorComponent,
    CdwDialogModule,
    CdwFileUploadModule,
    CdwIconModule,
    CommonUiComponentsSpinnerComponent,
  ],
})
export class UserCollectionCsvUploadDialogComponent {
  public readonly gray50: string = TailwindColors.gray50;
  public readonly gray200: string = TailwindColors.gray200;
  public readonly gray300: string = TailwindColors.gray300;
  public readonly gray400: string = TailwindColors.gray400;
  public readonly ctaBlue: string = TailwindColors.tertiaryBlueCta;
  public readonly greenDark: string = TailwindColors.tertiaryGreenDark;
  uploadedFile: File | null = null;
  validationErrors: string[] = [];
  validationWarnings: CsvValidationWarning[] = [];
  validatedData: Array<{ userCollectionName?: string; description?: string; email: string; rowNumber: number }> = [];
  isValidating = false;
  isImporting = false;
  isUploading = false;
  showWelcomeScreen = true;
  currentStep = 1; // 1: Upload, 2: Validate, 3: Import
  importMode: 'append' | 'replace' | '' = '';
  descriptionUpdateMode: 'update' | 'keep' | '' = '';

  // Email extraction mode - if true, only extract emails and return them instead of importing user collections
  emailExtractionMode = false;

  private readonly csvValidationService = inject(CsvValidationService);
  private readonly http = inject(HttpClient);
  private readonly snackbarService = inject(SnackbarService);
  private readonly urlBuilder = inject(ApiUrlBuilderService);

  constructor(
    public dialogRef: MatDialogRef<UserCollectionCsvUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: { emailExtractionMode?: boolean }
  ) {
    this.emailExtractionMode = data?.emailExtractionMode ?? false;
  }

  onFileSelected(file: File | File[] | null): void {
    if (file && !Array.isArray(file)) {
      this.uploadedFile = file;
    } else {
      this.uploadedFile = null;
    }
  }

  canContinueFromStep1(): boolean {
    return this.uploadedFile !== null;
  }

  canContinueFromStep2(): boolean {
    // Can continue if there's valid data to import, even if some rows had errors
    return this.validatedData.length > 0;
  }

  continueToNextStep(): void {
    if (this.currentStep === 1 && this.canContinueFromStep1()) {
      this.validateFile();
    } else if (this.currentStep === 2 && this.canContinueFromStep2()) {
      this.importData();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Two-Layer CSV Validation Architecture
   *
   * LAYER 1 (Frontend - CsvValidationService):
   * - File size/type validation
   * - CSV structure and parsing
   * - Email format validation
   * - Required field validation
   * - Duplicate emails within CSV
   * → Provides immediate feedback without server round-trip
   *
   * LAYER 2 (Backend - UserCollectionsService.validateCsvData):
   * - Checks if user collections already exist in database
   * - Detects description mismatches between CSV and database
   * - Validates users exist in system
   * - Business rule validation
   * → Requires database access, runs only if Layer 1 passes
   *
   * Errors and warnings from both layers are combined and shown to user.
   */
  async validateFile(): Promise<void> {
    if (!this.uploadedFile) return;

    this.isValidating = true;
    this.isUploading = true;
    this.validationErrors = [];
    this.validationWarnings = [];

    try {
      // LAYER 1: Frontend validation (immediate feedback without database)
      const frontendValidation: CsvValidationResult =
        await this.csvValidationService.validateCsvFile(
          this.uploadedFile,
          this.emailExtractionMode
        );

      // LAYER 2: Backend validation (business rules with database access)
      // Continue to backend validation if we have any valid data, even if some rows had errors
      if (frontendValidation.data && frontendValidation.data.length > 0) {
        const backendValidation = await firstValueFrom(
          this.http.post<CsvValidationResult>(
            this.urlBuilder.backendApi('user-collections', 'validate-csv'),
            {
              rows: frontendValidation.data,
              emailOnlyMode: this.emailExtractionMode
            }
          )
        );

        this.validationErrors = [
          ...frontendValidation.errors,
          ...backendValidation.errors,
        ];
        this.validationWarnings = [
          ...frontendValidation.warnings,
          ...backendValidation.warnings,
        ];

        // Store validated data for import
        this.validatedData = frontendValidation.data;
      } else {
        // No valid data - only show frontend errors
        this.validationErrors = frontendValidation.errors;
        this.validationWarnings = frontendValidation.warnings;
      }

      this.isUploading = false;
      this.isValidating = false;
      this.currentStep = 2;
    } catch (_error) {
      this.validationErrors.push(
        'An error occurred during validation. Please try again.'
      );
      this.isUploading = false;
      this.isValidating = false;
      this.currentStep = 2;
    }
  }

  async importData(): Promise<void> {
    // If in email extraction mode, just return the emails
    if (this.emailExtractionMode) {
      const emails = this.validatedData.map(row => row.email);
      this.dialogRef.close({ success: true, emails });
      return;
    }

    // Otherwise, proceed with normal import
    this.isImporting = true;
    this.currentStep = 3;

    try {
      const hasExistingCollections = this.validationWarnings.some(w => w.type === 'existing_collections');
      const hasDescriptionMismatch = this.validationWarnings.some(w => w.type === 'description_mismatch');

      await firstValueFrom(
        this.http.post(
          this.urlBuilder.backendApi('user-collections', 'import-csv'),
          {
            rows: this.validatedData,
            mode: hasExistingCollections ? this.importMode : 'append',
            descriptionUpdateMode: hasDescriptionMismatch ? this.descriptionUpdateMode : undefined,
          }
        )
      );

      this.isImporting = false;
      this.snackbarService.showMessage(
        'User collection(s) have been successfully imported.',
        'OK',
        {
          horizontalPosition: 'center',
        }
      );
      this.dialogRef.close({ success: true });
    } catch (_error) {
      this.isImporting = false;
      this.snackbarService.showError(
        'An error occurred during import. Please try again.',
        'OK',
        {
          horizontalPosition: 'center',
        }
      );
    }
  }

  downloadTemplate(): void {
    // Define example emails once to avoid duplication
    const exampleEmails = [
      'john.doe@company.com',
      'jane.smith@company.com',
      'sarah.jones@company.com',
      'michael.brown@company.com',
      'emily.davis@company.com',
      'david.wilson@company.com',
    ];

    let csvContent: string[][];
    let filename: string;

    if (this.emailExtractionMode) {
      // Email-only template for adding users to existing collection
      csvContent = [
        ['Email'],
        ...exampleEmails.map(email => [email])
      ];
      filename = 'user_emails_template.csv';
    } else {
      // Full template for creating/updating user collections
      csvContent = [
        ['User Collection Name', 'Description', 'Email'],
        ['Engineering Team', 'Software development and engineering staff', exampleEmails[0]],
        ['Engineering Team', '', exampleEmails[1]],
        ['Marketing Team', 'Marketing and communications team', exampleEmails[2]],
        ['Marketing Team', '', exampleEmails[3]],
        ['Sales Team', 'Sales and business development representatives', exampleEmails[4]],
        ['Sales Team', '', exampleEmails[5]],
      ];
      filename = 'user_collections_template.csv';
    }

    // Convert to CSV format
    const csv = csvContent
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }

  get isContinueDisabled(): boolean {
    if (this.currentStep === 1) {
      return !this.canContinueFromStep1() || this.isValidating;
    } else if (this.currentStep === 2) {
      if (this.validatedData.length === 0) return true;
      if (this.validationErrors.length > 0) return true;
      if (this.hasUnresolvedWarnings) return true;
      return false;
    } else if (this.currentStep === 3) {
      return this.isImporting;
    }

    return false;
  }

  get hasUnresolvedWarnings(): boolean {
    const needsImportMode = this.validationWarnings.some(
      (w) => w.type === 'existing_collections'
    );
    const needsDescriptionMode = this.validationWarnings.some(
      (w) => w.type === 'description_mismatch'
    );
    if (needsImportMode && !this.importMode) return true;
    if (needsDescriptionMode && !this.descriptionUpdateMode) return true;
    return false;
  }

  get continueButtonLabel(): string {
    if (this.currentStep === 1) {
      return 'Continue';
    } else if (this.currentStep === 2) {
      return 'Continue';
    } else {
      return 'Done';
    }
  }

  handleContinue(): void {
    if (this.showWelcomeScreen) {
      // Transition from welcome screen to stepper
      this.showWelcomeScreen = false;
      return;
    }

    if (this.currentStep === 3) {
      // On final step, close dialog
      this.dialogRef.close({ success: true });
    } else {
      this.continueToNextStep();
    }
  }

  goToStep(step: number): void {
    // Only allow navigation to current step or previous steps
    if (step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  get dialogHeight(): string {
    // Make dialog taller on step 2 to accommodate errors/warnings
    // Use min() to cap at 85vh so the dialog never exceeds the viewport on small screens
    return this.currentStep === 2 && !this.showWelcomeScreen
      ? 'min(800px, 85vh)'
      : 'min(500px, 85vh)';
  }
}
